import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useScrollLock from '../../../hooks/useScrollLock';
import { categoryPath, flattenCategoryTree } from '../../../services/storefront';
import { IconArrowRight, IconChevronRight, IconClose, IconSearch } from '../../storefront/icons';

const MAX_CATEGORY_RESULTS = 7;

// Command-palette style search. Category matches come from the already-loaded
// category list (no request per keystroke); Enter on the first row runs the
// normal product search on /products.
const SearchPalette = ({ open, onClose, roots, popular }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  useScrollLock(open);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIndex(0);
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  const entries = useMemo(() => flattenCategoryTree(roots), [roots]);
  const trimmed = query.trim();

  const results = useMemo(() => {
    if (!trimmed) return [];
    const needle = trimmed.toLowerCase();
    // Word-start matches ("Small Hides" for "hide") rank above mid-word
    // ones ("Cowhides"), then shallower categories, then A–Z.
    const categoryMatches = entries
      .map((entry) => {
        const position = entry.name.toLowerCase().indexOf(needle);
        const wordStart = position === 0 || /\s/.test(entry.name[position - 1] || '');
        return { ...entry, position, rank: wordStart ? 0 : 1 };
      })
      .filter((entry) => entry.position !== -1)
      .sort((a, b) => a.rank - b.rank
        || a.trail.length - b.trail.length
        || a.name.localeCompare(b.name))
      .slice(0, MAX_CATEGORY_RESULTS)
      .map((entry) => ({
        key: entry.id,
        kind: 'category',
        label: entry.name,
        trail: entry.trail,
        to: categoryPath(entry.id),
      }));
    return [
      {
        key: 'search',
        kind: 'search',
        label: trimmed,
        to: `/products?search=${encodeURIComponent(trimmed)}`,
      },
      ...categoryMatches,
    ];
  }, [entries, trimmed]);

  useEffect(() => {
    setActiveIndex(0);
  }, [trimmed]);

  if (!open) return null;

  const go = (to) => {
    onClose();
    navigate(to);
  };

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    } else if (event.key === 'ArrowDown' && results.length) {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % results.length);
    } else if (event.key === 'ArrowUp' && results.length) {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + results.length) % results.length);
    } else if (event.key === 'Enter' && results[activeIndex]) {
      event.preventDefault();
      go(results[activeIndex].to);
    }
  };

  const categoryCount = results.length - 1;

  return (
    <div className="tp-palette" role="dialog" aria-modal="true" aria-label="Search the catalog">
      <div className="tp-palette__scrim" onClick={onClose} aria-hidden="true" />
      <div className="tp-palette__panel" onKeyDown={onKeyDown}>
        <div className="tp-palette__field">
          <IconSearch size={22} />
          <input
            ref={inputRef}
            className="tp-palette__input"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search cowhides, poufs, onyx, taxidermy…"
            aria-label="Search products and collections"
            aria-activedescendant={results.length ? `tp-palette-item-${activeIndex}` : undefined}
            aria-controls="tp-palette-results"
            autoComplete="off"
            spellCheck="false"
          />
          <button type="button" className="tp-palette__close" onClick={onClose} aria-label="Close search">
            <IconClose size={18} />
          </button>
        </div>

        <div className="tp-palette__body" id="tp-palette-results">
          {trimmed ? (
            <ul className="tp-palette__list" role="listbox" aria-label="Search results">
              {results.map((result, i) => (
                <React.Fragment key={result.key}>
                  {i === 1 && <li className="tp-palette__group" role="presentation">Collections</li>}
                  <li
                    id={`tp-palette-item-${i}`}
                    role="option"
                    aria-selected={i === activeIndex}
                    className={`tp-palette__item${i === activeIndex ? ' is-active' : ''}`}
                    onMouseMove={() => setActiveIndex(i)}
                    onClick={() => go(result.to)}
                  >
                    {result.kind === 'search' ? (
                      <>
                        <span className="tp-palette__item-icon"><IconSearch size={17} /></span>
                        <span className="tp-palette__item-text tp-palette__item-text--inline">
                          Search all products for <strong>“{result.label}”</strong>
                        </span>
                        <kbd className="tp-palette__kbd">Enter</kbd>
                      </>
                    ) : (
                      <>
                        <span className="tp-palette__item-icon"><IconChevronRight size={17} /></span>
                        <span className="tp-palette__item-text">
                          <span className="tp-palette__item-label">{result.label}</span>
                          {result.trail.length > 0 && (
                            <span className="tp-palette__trail">{result.trail.join(' › ')}</span>
                          )}
                        </span>
                        <IconArrowRight size={16} className="tp-palette__item-go" />
                      </>
                    )}
                  </li>
                </React.Fragment>
              ))}
              {categoryCount === 0 && (
                <li className="tp-palette__hint" role="presentation">
                  No collection names match — press Enter to search every product.
                </li>
              )}
            </ul>
          ) : (
            <div className="tp-palette__empty">
              {popular.length > 0 && (
                <>
                  <p className="tp-palette__group">Popular collections</p>
                  <div className="tp-palette__chips">
                    {popular.map((node) => (
                      <Link key={node.id} to={categoryPath(node.id)} className="tp-palette__chip" onClick={onClose}>
                        {node.name}
                      </Link>
                    ))}
                  </div>
                </>
              )}
              <p className="tp-palette__group">Jump to</p>
              <div className="tp-palette__chips">
                <Link to="/products" className="tp-palette__chip" onClick={onClose}>All products</Link>
                <Link to="/wholesale" className="tp-palette__chip" onClick={onClose}>Trade program</Link>
                <Link to="/contact" className="tp-palette__chip" onClick={onClose}>Contact us</Link>
              </div>
            </div>
          )}
        </div>

        <div className="tp-palette__foot" aria-hidden="true">
          <span><kbd className="tp-palette__kbd">↑</kbd><kbd className="tp-palette__kbd">↓</kbd> to move</span>
          <span><kbd className="tp-palette__kbd">Enter</kbd> to open</span>
          <span><kbd className="tp-palette__kbd">Esc</kbd> to close</span>
        </div>
      </div>
    </div>
  );
};

export default SearchPalette;
